import React, { useState, useEffect } from 'react';
import { appointmentsAPI } from '../services/api';

const CATEGORY_COLORS = {
  Medical:  'var(--accent-teal)',
  Dental:   'var(--accent-teal)',
  School:   'var(--accent-teal)',
  Work:     'var(--accent-teal)',
  Personal: 'var(--accent-teal)',
  Other:    'var(--accent-teal)',
};

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

// mysql2 serialises DATETIME as UTC ISO strings ("...Z") even though the value
// was stored without timezone. Strip the Z so JS parses it as local time.
function parseLocalDt(dt) {
  if (!dt) return null;
  return new Date(dt.replace(' ', 'T').replace(/Z$/, ''));
}

function formatTime(dt) {
  if (!dt) return '';
  return parseLocalDt(dt).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

function AppointmentCalendar() {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => { fetchAppointments(); }, []);

  const fetchAppointments = async () => {
    try {
      const res = await appointmentsAPI.getAll();
      setAppointments(res.data);
    } catch (err) {
      console.error('Error fetching appointments:', err);
    } finally {
      setLoading(false);
    }
  };

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfWeek = new Date(year, month, 1).getDay();

  const monthAppointments = appointments.filter(apt => {
    const d = parseLocalDt(apt.appointment_date);
    return d.getFullYear() === year && d.getMonth() === month;
  });

  const appointmentsByDay = {};
  monthAppointments.forEach(apt => {
    const day = parseLocalDt(apt.appointment_date).getDate();
    if (!appointmentsByDay[day]) appointmentsByDay[day] = [];
    appointmentsByDay[day].push(apt);
  });

  const cells = [];
  for (let i = 0; i < firstDayOfWeek; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const today = new Date();
  const isToday = (day) =>
    day === today.getDate() &&
    month === today.getMonth() &&
    year === today.getFullYear();

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const goToToday = () => { setCurrentDate(new Date()); setSelectedDay(null); };

  const selectedApts = selectedDay ? (appointmentsByDay[selectedDay] || []) : [];

  if (loading) return <div className="loading"><div className="spinner"></div></div>;

  return (
    <div className="container fade-in">
      <div className="page-header">
        <div>
          <h2>Appointment Calendar</h2>
          <p>Monthly overview of all appointments</p>
        </div>
        <button onClick={goToToday} className="btn btn-secondary">Today</button>
      </div>

      <div className="cal-layout">
        <div className="card" style={{ flex: 1, minWidth: 0, padding: 0, overflow: 'hidden' }}>
          {/* Month navigation */}
          <div className="cal-month-header">
            <button className="cal-nav-btn" onClick={prevMonth}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6"/>
              </svg>
            </button>
            <span className="cal-month-title">
              {MONTH_NAMES[month]} {year}
            </span>
            <button className="cal-nav-btn" onClick={nextMonth}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </button>
          </div>

          {/* Day-of-week headers */}
          <div className="cal-grid">
            {DAY_NAMES.map(d => (
              <div key={d} className="cal-day-header">{d}</div>
            ))}

            {/* Calendar cells */}
            {cells.map((day, idx) => {
              if (day === null) {
                return <div key={`empty-${idx}`} className="cal-cell cal-cell-empty" />;
              }
              const apts = appointmentsByDay[day] || [];
              const todayCell = isToday(day);
              const selected = selectedDay === day;
              return (
                <div
                  key={day}
                  className={`cal-cell${todayCell ? ' cal-today' : ''}${selected ? ' cal-selected' : ''}${apts.length ? ' cal-has-apts' : ''}`}
                  onClick={() => setSelectedDay(selected ? null : day)}
                >
                  <div className={`cal-day-num${todayCell ? ' cal-today-num' : ''}`}>{day}</div>
                  <div className="cal-apt-list">
                    {apts.slice(0, 3).map(apt => (
                      <div
                        key={apt.id}
                        className="cal-apt-pill"
                        style={{
                          background: `${CATEGORY_COLORS[apt.category] || 'var(--accent-coral)'}22`,
                          color: CATEGORY_COLORS[apt.category] || 'var(--accent-coral)',
                          borderLeft: `2px solid ${CATEGORY_COLORS[apt.category] || 'var(--accent-coral)'}`,
                        }}
                        title={`${apt.title} — ${formatTime(apt.appointment_date)}`}
                      >
                        {apt.title}
                      </div>
                    ))}
                    {apts.length > 3 && (
                      <div className="cal-apt-more">+{apts.length - 3} more</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Day detail panel */}
        {selectedDay && (
          <div className="card cal-detail-panel">
            <div style={{
              padding: '16px 20px 12px', borderBottom: '1px solid var(--border-subtle)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)', fontWeight: 600 }}>
                  {MONTH_NAMES[month]} {selectedDay}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                  {selectedApts.length} appointment{selectedApts.length !== 1 ? 's' : ''}
                </div>
              </div>
              <button
                onClick={() => setSelectedDay(null)}
                style={{
                  background: 'none', border: 'none', color: 'var(--text-muted)',
                  cursor: 'pointer', padding: 4, lineHeight: 1,
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            {selectedApts.length === 0 ? (
              <div style={{ padding: '24px 20px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                No appointments
              </div>
            ) : (
              <div style={{ overflowY: 'auto', flex: 1 }}>
                {selectedApts.map(apt => {
                  const color = CATEGORY_COLORS[apt.category] || 'var(--accent-coral)';
                  return (
                    <div key={apt.id} style={{
                      padding: '12px 20px', borderBottom: '1px solid var(--border-subtle)',
                      borderLeft: `3px solid ${color}`,
                    }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', marginBottom: 4 }}>
                        {apt.title}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        {formatTime(apt.appointment_date)}
                        {apt.end_time && ` — ${formatTime(apt.end_time)}`}
                      </div>
                      {apt.location && (
                        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                          {apt.location}
                        </div>
                      )}
                      {apt.category && (
                        <div style={{
                          display: 'inline-block', marginTop: 6, fontSize: 10, fontWeight: 700,
                          padding: '2px 7px', borderRadius: 4,
                          background: `${color}22`, color, border: `1px solid ${color}44`,
                          textTransform: 'uppercase', letterSpacing: '0.06em',
                        }}>
                          {apt.category}
                        </div>
                      )}
                      {apt.description && (
                        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                          {apt.description}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default AppointmentCalendar;
